"use client";

/**
 * Quiet share controls for System Card. "chips" is the one row of outlined
 * chips under the edition byline; "row" is the small muted text row under a
 * card or section. Kept deliberately quiet: no card, no filled buttons. A loud
 * share card and a bare text line were both rejected in review (LEV-2913).
 */
import { useEffect, useRef, useState } from "react";

import { trackLearnSystemCardShare, type LearnSystemCardShareChannel } from "@/lib/analytics/client";
import { linkedInShareUrl, systemCardMailto } from "@/lib/learn/systemCardShare";

export interface ShareRowProps {
  /** "chips": the header row under the byline. "row": the small muted row under a card or section. */
  variant: "chips" | "row";
  /** Absolute URL being shared (canonical edition URL, optionally with #anchor). */
  url: string;
  /** Email subject and LinkedIn title. */
  title: string;
  /** Plain text copied by "Copy" and used as the email body, credit line already included. */
  text: string;
  /** Edition slug and section id, sent with the share analytics event. */
  slug: string;
  section: string;
}

const COPIED_RESET_MS = 1600;

/** Resolves true once the text is on the clipboard; falls back to execCommand where the async API is missing or refused. */
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // expected: clipboard permission denied or insecure context; the execCommand fallback below handles it.
  }
  const area = document.createElement("textarea");
  area.value = text;
  area.setAttribute("readonly", "");
  area.style.position = "fixed";
  area.style.opacity = "0";
  document.body.appendChild(area);
  area.select();
  try {
    return document.execCommand("copy");
  } catch {
    // expected: no clipboard access at all; the button reports "Copy failed".
    return false;
  } finally {
    document.body.removeChild(area);
  }
}

const emailCopy = (subject: string, body: string) => `Subject: ${subject}\n\n${body}`;

const CHIP =
  "inline-flex items-center rounded-full border border-slate-300 px-3 py-1 text-[12.5px] font-semibold leading-[1.3] text-landing-dark transition-colors hover:border-lev-orange hover:text-lev-orange focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lev-orange/40";
const CHIP_ACCENT =
  "inline-flex items-center rounded-full border border-lev-orange px-3 py-1 text-[12.5px] font-semibold leading-[1.3] text-lev-orange transition-colors hover:bg-lev-orange-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lev-orange/40";
const TEXT_BUTTON =
  "rounded text-landing-secondary underline decoration-slate-300 underline-offset-[3px] transition-colors hover:text-lev-orange hover:decoration-lev-orange focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lev-orange/40";
const TEXTAREA =
  "min-h-[96px] w-full resize-y rounded-md border border-slate-300 bg-white px-3 py-2 text-[14px] leading-[1.5] text-landing-dark focus:border-lev-orange focus:outline-none focus:ring-2 focus:ring-lev-orange/25";

export function ShareRow({ variant, url, title, text, slug, section }: ShareRowProps) {
  // Editable only in the chips variant's "Show text" panel; the row variant always shares `text`.
  const [postText, setPostText] = useState(text);
  const [emailBody, setEmailBody] = useState(text);
  const [status, setStatus] = useState<{ channel: LearnSystemCardShareChannel; ok: boolean } | null>(null);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (resetTimer.current) clearTimeout(resetTimer.current);
    },
    [],
  );

  const track = (channel: LearnSystemCardShareChannel) => trackLearnSystemCardShare({ slug, section, channel });

  const copy = async (channel: LearnSystemCardShareChannel, value: string) => {
    track(channel);
    const ok = await copyText(value);
    if (resetTimer.current) clearTimeout(resetTimer.current);
    setStatus({ channel, ok });
    resetTimer.current = setTimeout(() => setStatus(null), COPIED_RESET_MS);
  };

  const label = (channel: LearnSystemCardShareChannel, idle: string) =>
    status?.channel === channel ? (status.ok ? "Copied" : "Copy failed") : idle;

  const announcement = status ? (status.ok ? "Copied to clipboard" : "Copy failed") : "";
  const live = (
    <span role="status" aria-live="polite" className="sr-only">
      {announcement}
    </span>
  );

  if (variant === "row") {
    return (
      <div
        className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-2.5 text-[12px] text-landing-secondary"
        data-testid="share-row"
      >
        <span>Share</span>
        <button type="button" className={TEXT_BUTTON} onClick={() => void copy("copy_post", text)}>
          {label("copy_post", "Copy")}
        </button>
        <a
          href={linkedInShareUrl(url)}
          target="_blank"
          rel="noopener noreferrer"
          className={TEXT_BUTTON}
          onClick={() => track("linkedin")}
        >
          LinkedIn
        </a>
        <a href={systemCardMailto(title, text)} className={TEXT_BUTTON} onClick={() => track("email")}>
          Email
        </a>
        <button type="button" className={TEXT_BUTTON} onClick={() => void copy("copy_link", url)}>
          {label("copy_link", "Copy link")}
        </button>
        {live}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2" data-testid="share-chips">
      <div className="flex flex-wrap items-center gap-2">
        <span className="mr-1 text-[12.5px] text-landing-secondary">Share this:</span>
        <a
          href={linkedInShareUrl(url)}
          target="_blank"
          rel="noopener noreferrer"
          className={CHIP_ACCENT}
          onClick={() => track("linkedin")}
        >
          Share on LinkedIn
        </a>
        <button type="button" className={CHIP} onClick={() => void copy("copy_post", postText)}>
          {label("copy_post", "Copy a LinkedIn post")}
        </button>
        <button type="button" className={CHIP} onClick={() => void copy("copy_email", emailCopy(title, emailBody))}>
          {label("copy_email", "Copy an email")}
        </button>
        <a href={systemCardMailto(title, emailBody)} className={CHIP} onClick={() => track("email")}>
          Email it
        </a>
        <button type="button" className={CHIP} onClick={() => void copy("copy_link", url)}>
          {label("copy_link", "Copy link")}
        </button>
        {live}
      </div>
      <details className="text-[12.5px] text-landing-secondary">
        <summary className="w-fit cursor-pointer select-none hover:text-lev-orange">Show text</summary>
        <div className="mt-2 flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span>LinkedIn post</span>
            <textarea className={TEXTAREA} value={postText} onChange={(e) => setPostText(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1">
            <span>Email (subject: {title})</span>
            <textarea className={TEXTAREA} value={emailBody} onChange={(e) => setEmailBody(e.target.value)} />
          </label>
        </div>
      </details>
    </div>
  );
}
